/**
 * OurNet Geocentric Queries - DEPRECATED (Locality System)
 *
 * ⚠️  DEPRECATION NOTICE (March 11, 2026):
 * Most functions in this file use the OLD locality-based system (user_area_localities → localities).
 * The platform now uses the NEW boundary-based system (user_areas.boundary_ids → admin_boundaries).
 *
 * ACTIVE FUNCTIONS (still in use):
 * - reverseGeocodeToLocality() - Used for guest user IP geolocation fallback
 * - calculateDistance() - General utility function
 *
 * DEPRECATED FUNCTIONS (do not use):
 * - getArticlesForLocality() - Use articleService.getLocalityNews() with boundaryIds instead
 * - getArticlesForArea() - Calls old RPC 'get_articles_for_user_area' (replaced by 'get_articles_for_user_boundaries')
 * - All user_area_localities CRUD functions - Boundary system uses boundary_ids array directly
 *
 * This file is retained for backward compatibility. Do not add new code here.
 */

import { supabaseFetch } from '../lib/supabase';

/**
 * Get articles near a specific locality
 *
 * @param {string} localityId - UUID of the locality
 * @param {Object} options - Query options
 * @param {number} options.limit - Max articles to return (default: 50)
 * @param {number} options.maxAgeDays - Max age of articles in days (default: 30)
 * @returns {Promise<Array>} - Array of articles with distance info
 */
export async function getArticlesForLocality(localityId, options = {}) {
  const { limit = 50, maxAgeDays = 30 } = options;

  try {
    // Get locality details
    const { data: locality, error: localityError } = await supabaseFetch.query('localities', {
      select: 'latitude,longitude,coverage_radius_meters,name',
      eq: { id: localityId },
      single: true
    });

    if (localityError || !locality) {
      console.error('Error fetching locality:', localityError);
      return [];
    }

    // Query articles within radius using PostGIS function
    const { data: articles, error: articlesError } = await supabaseFetch.rpc('get_articles_near_point', {
      center_lat: locality.latitude,
      center_lng: locality.longitude,
      radius_meters: locality.coverage_radius_meters,
      limit_count: limit
    });

    if (articlesError) {
      console.error('Error fetching articles:', articlesError);
      return [];
    }

    return articles || [];

  } catch (error) {
    console.error('Error in getArticlesForLocality:', error);
    return [];
  }
}

/**
 * Get articles for a user area (multiple localities combined)
 *
 * @param {string} areaId - UUID of the user area
 * @param {Object} options - Query options
 * @param {number} options.limit - Max articles to return (default: 50)
 * @returns {Promise<Array>} - Array of articles from all localities in the area
 */
export async function getArticlesForArea(areaId, options = {}) {
  const { limit = 50 } = options;

  try {
    // Use PostGIS function that handles multiple localities
    const { data: articles, error } = await supabaseFetch.rpc('get_articles_for_user_area', {
      area_uuid: areaId,
      limit_count: limit
    });

    if (error) {
      console.error('Error fetching articles for area:', error);
      return [];
    }

    return articles || [];

  } catch (error) {
    console.error('Error in getArticlesForArea:', error);
    return [];
  }
}

/**
 * Get user's current location using browser geolocation API
 *
 * @returns {Promise<Object>} - Coordinates object {latitude, longitude}
 */
export async function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
}

/**
 * Find nearest locality to given coordinates
 * Useful for auto-suggesting locality when user posts content
 *
 * @param {number} latitude - User's latitude
 * @param {number} longitude - User's longitude
 * @returns {Promise<Object|null>} - Nearest locality object
 */
export async function findNearestLocality(latitude, longitude) {
  try {
    // Use supabaseFetch for reliable queries (supabase client can hang)
    const { data: localities, error } = await supabaseFetch.query('localities', {
      select: 'id, name, municipality, latitude, longitude, coverage_radius_meters',
      limit: 100 // Get all localities (Silver Coast has ~23)
    });

    if (error || !localities) {
      console.error('Error fetching localities:', error);
      return null;
    }

    // Calculate distances client-side (simple haversine approximation)
    // For MVP, this is acceptable since we have few localities
    const withDistances = localities.map(locality => ({
      ...locality,
      distance: calculateDistance(
        latitude,
        longitude,
        locality.latitude,
        locality.longitude
      )
    }));

    // Sort by distance
    withDistances.sort((a, b) => a.distance - b.distance);

    // Return nearest
    return withDistances[0] || null;

  } catch (error) {
    console.error('Error in findNearestLocality:', error);
    return null;
  }
}

/**
 * Reverse geocode coordinates to locality name
 * Gets the locality that contains these coordinates (within its radius)
 *
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<Object|null>} - Locality object or null if outside all localities
 */
export async function reverseGeocodeToLocality(latitude, longitude) {
  try {
    // Use supabaseFetch for reliable queries (supabase client can hang)
    const { data: localities, error } = await supabaseFetch.query('localities', {
      select: 'id, name, municipality, latitude, longitude, coverage_radius_meters'
    });

    if (error || !localities) {
      console.error('Error fetching localities:', error);
      return null;
    }

    // Find locality whose coverage area contains this point
    for (const locality of localities) {
      const distance = calculateDistance(
        latitude,
        longitude,
        locality.latitude,
        locality.longitude
      );

      // If within coverage radius, this is the locality
      if (distance <= locality.coverage_radius_meters) {
        return {
          ...locality,
          distance
        };
      }
    }

    // Not within any locality - return nearest instead
    return findNearestLocality(latitude, longitude);

  } catch (error) {
    console.error('Error in reverseGeocodeToLocality:', error);
    return null;
  }
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in meters
 *
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} - Distance in meters
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Format distance for display
 *
 * @param {number} meters - Distance in meters
 * @returns {string} - Formatted distance string
 */
export function formatDistance(meters) {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  } else {
    return `${(meters / 1000).toFixed(1)}km`;
  }
}

/**
 * Create article with geolocation
 * Automatically detects user's location and finds nearest locality
 *
 * @param {Object} articleData - Article data (title, content, etc.)
 * @param {Object} options - Options
 * @param {boolean} options.autoDetectLocation - Auto-detect user location (default: true)
 * @param {Object} options.manualLocation - Manual coordinates {latitude, longitude}
 * @returns {Promise<Object>} - Created article object
 */
export async function createArticleWithLocation(articleData, options = {}) {
  const { autoDetectLocation = true, manualLocation = null, userId = null } = options;

  try {
    let coordinates;

    // Get coordinates
    if (manualLocation) {
      coordinates = manualLocation;
    } else if (autoDetectLocation) {
      coordinates = await getCurrentLocation();
    } else {
      throw new Error('No location provided');
    }

    // Find locality for these coordinates
    const locality = await reverseGeocodeToLocality(
      coordinates.latitude,
      coordinates.longitude
    );

    // Get user ID from options or localStorage (avoid auth.getUser which hangs)
    let authorId = userId;
    if (!authorId) {
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        throw new Error('User not authenticated');
      }
      // Decode JWT to get user ID
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        authorId = payload.sub;
      } catch (e) {
        throw new Error('Invalid auth token');
      }
    }

    // Create article with coordinates
    const { data: article, error: createError } = await supabaseFetch.insert('articles', {
      ...articleData,
      author_id: authorId,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      location_point: `POINT(${coordinates.longitude} ${coordinates.latitude})`
    });

    if (createError) {
      throw createError;
    }

    return {
      ...article,
      detected_locality: locality
    };

  } catch (error) {
    console.error('Error in createArticleWithLocation:', error);
    throw error;
  }
}

/**
 * Get all user areas for current user
 *
 * @param {string} userId - Optional user ID (if not provided, reads from localStorage)
 * @returns {Promise<Array>} - Array of user areas with their localities
 */
export async function getUserAreas(userId = null) {
  try {
    // Get user ID from parameter or localStorage (avoid auth.getUser which hangs)
    let uid = userId;
    if (!uid) {
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        return [];
      }
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        uid = payload.sub;
      } catch (e) {
        return [];
      }
    }

    // Step 1: Get user areas
    const { data: areas, error } = await supabaseFetch.query('user_areas', {
      select: '*',
      eq: { user_id: uid },
      order: 'created_at.desc'
    });

    if (error) {
      console.error('Error fetching user areas:', error);
      return [];
    }

    if (!areas || areas.length === 0) {
      return [];
    }

    // Step 2: Get area localities for all areas
    const areaIds = areas.map(a => a.id);
    const { data: areaLocalities } = await supabaseFetch.query('user_area_localities', {
      select: 'area_id,locality_id',
      inFilter: { area_id: areaIds }
    });

    // Step 3: Get localities details
    const localityIds = [...new Set((areaLocalities || []).map(al => al.locality_id))];
    let localitiesMap = {};
    if (localityIds.length > 0) {
      const { data: localities } = await supabaseFetch.query('localities', {
        select: 'id,name,municipality,latitude,longitude,coverage_radius_meters',
        inFilter: { id: localityIds }
      });
      localitiesMap = (localities || []).reduce((acc, l) => {
        acc[l.id] = l;
        return acc;
      }, {});
    }

    // Step 4: Combine data
    return areas.map(area => ({
      ...area,
      user_area_localities: (areaLocalities || [])
        .filter(al => al.area_id === area.id)
        .map(al => ({
          locality_id: al.locality_id,
          localities: localitiesMap[al.locality_id] || null
        }))
    }));

  } catch (error) {
    console.error('Error in getUserAreas:', error);
    return [];
  }
}

/**
 * Get user's home area
 *
 * @param {string} userId - Optional user ID (if not provided, reads from localStorage)
 * @returns {Promise<Object|null>} - Home area object or null
 */
export async function getUserHomeArea(userId = null) {
  try {
    // Get user ID from parameter or localStorage (avoid auth.getUser which hangs)
    let uid = userId;
    if (!uid) {
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        return null;
      }
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        uid = payload.sub;
      } catch (e) {
        return null;
      }
    }

    // Step 1: Get home area
    const { data: homeArea, error } = await supabaseFetch.query('user_areas', {
      select: '*',
      eq: { user_id: uid, is_home: true },
      single: true
    });

    if (error || !homeArea) {
      console.error('Error fetching home area:', error);
      return null;
    }

    // Step 2: Get area localities
    const { data: areaLocalities } = await supabaseFetch.query('user_area_localities', {
      select: 'locality_id',
      eq: { area_id: homeArea.id }
    });

    // Step 3: Get localities details
    const localityIds = (areaLocalities || []).map(al => al.locality_id);
    let localities = [];
    if (localityIds.length > 0) {
      const { data: localitiesData } = await supabaseFetch.query('localities', {
        select: 'id,name,municipality,latitude,longitude,coverage_radius_meters',
        inFilter: { id: localityIds }
      });
      localities = localitiesData || [];
    }

    // Combine data
    return {
      ...homeArea,
      user_area_localities: (areaLocalities || []).map(al => ({
        locality_id: al.locality_id,
        localities: localities.find(l => l.id === al.locality_id) || null
      }))
    };

  } catch (error) {
    console.error('Error in getUserHomeArea:', error);
    return null;
  }
}

/**
 * Create user area
 *
 * @param {Object} areaData - Area data
 * @param {string} areaData.area_code - 6-character area code (e.g., "HOME01")
 * @param {string} areaData.display_name - Display name (e.g., "My Daily Area")
 * @param {string} areaData.description - Optional description
 * @param {boolean} areaData.is_home - Is this the home area?
 * @param {Array<string>} localityIds - Array of locality UUIDs to add to area
 * @param {string} userId - Optional user ID (if not provided, reads from localStorage)
 * @returns {Promise<Object>} - Created area object
 */
export async function createUserArea(areaData, localityIds = [], userId = null) {
  try {
    // Get user ID from parameter or localStorage (avoid auth.getUser which hangs)
    let uid = userId;
    if (!uid) {
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        throw new Error('User not authenticated');
      }
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        uid = payload.sub;
      } catch (e) {
        throw new Error('Invalid auth token');
      }
    }

    // Validate locality count
    if (localityIds.length > 6) {
      throw new Error('Maximum 6 localities per area');
    }

    // Create area
    const { data: area, error: areaError } = await supabaseFetch.insert('user_areas', {
      ...areaData,
      user_id: uid
    });

    if (areaError) {
      throw areaError;
    }

    // Add localities to area
    if (localityIds.length > 0) {
      for (let i = 0; i < localityIds.length; i++) {
        const { error: localityError } = await supabaseFetch.insert('user_area_localities', {
          area_id: area.id,
          locality_id: localityIds[i],
          display_order: i
        });

        if (localityError) {
          // Rollback: delete the area
          await supabaseFetch.delete('user_areas', { eq: { id: area.id } });
          throw localityError;
        }
      }
    }

    return area;

  } catch (error) {
    console.error('Error in createUserArea:', error);
    throw error;
  }
}

/**
 * Add locality to user area
 *
 * @param {string} areaId - UUID of the area
 * @param {string} localityId - UUID of the locality to add
 * @returns {Promise<boolean>} - Success status
 */
export async function addLocalityToArea(areaId, localityId) {
  try {
    const { error } = await supabaseFetch.insert('user_area_localities', {
      area_id: areaId,
      locality_id: localityId
    });

    if (error) {
      console.error('Error adding locality to area:', error);
      return false;
    }

    return true;

  } catch (error) {
    console.error('Error in addLocalityToArea:', error);
    return false;
  }
}

/**
 * Remove locality from user area
 *
 * @param {string} areaId - UUID of the area
 * @param {string} localityId - UUID of the locality to remove
 * @returns {Promise<boolean>} - Success status
 */
export async function removeLocalityFromArea(areaId, localityId) {
  try {
    // Find the record to delete (supabaseFetch.delete needs single eq)
    const { data: record, error: findError } = await supabaseFetch.query('user_area_localities', {
      select: 'id',
      eq: { area_id: areaId, locality_id: localityId },
      single: true
    });

    if (findError || !record) {
      console.error('Error finding locality link:', findError);
      return false;
    }

    const { error } = await supabaseFetch.delete('user_area_localities', {
      eq: { id: record.id }
    });

    if (error) {
      console.error('Error removing locality from area:', error);
      return false;
    }

    return true;

  } catch (error) {
    console.error('Error in removeLocalityFromArea:', error);
    return false;
  }
}

/**
 * Delete user area
 *
 * @param {string} areaId - UUID of the area to delete
 * @returns {Promise<boolean>} - Success status
 */
export async function deleteUserArea(areaId) {
  try {
    const { error } = await supabaseFetch.delete('user_areas', {
      eq: { id: areaId }
    });

    if (error) {
      console.error('Error deleting user area:', error);
      return false;
    }

    return true;

  } catch (error) {
    console.error('Error in deleteUserArea:', error);
    return false;
  }
}

/**
 * Get all available localities
 *
 * @returns {Promise<Array>} - Array of all localities
 */
export async function getAllLocalities() {
  try {
    // Use supabaseFetch for reliable queries (supabase client can hang)
    const { data: localities, error } = await supabaseFetch.query('localities', {
      select: '*',
      order: 'municipality.asc,name.asc'
    });

    if (error) {
      console.error('Error fetching localities:', error);
      return [];
    }

    return localities || [];

  } catch (error) {
    console.error('Error in getAllLocalities:', error);
    return [];
  }
}
